/** Prefijos de país para el teléfono de un cliente. Chile va primero y
 *  queda seleccionado por defecto —es donde vende NovaBerry casi
 *  siempre—, el resto son los países de Sudamérica con los que más se
 *  cruza un negocio chileno, más España y Estados Unidos para las
 *  llamadas ocasionales de fuera. */
export interface PaisTelefono {
  dial: string
  nombre: string
  bandera: string
}

export const PAISES_TELEFONO: PaisTelefono[] = [
  { dial: '+56', nombre: 'Chile', bandera: '🇨🇱' },
  { dial: '+54', nombre: 'Argentina', bandera: '🇦🇷' },
  { dial: '+51', nombre: 'Perú', bandera: '🇵🇪' },
  { dial: '+591', nombre: 'Bolivia', bandera: '🇧🇴' },
  { dial: '+57', nombre: 'Colombia', bandera: '🇨🇴' },
  { dial: '+593', nombre: 'Ecuador', bandera: '🇪🇨' },
  { dial: '+598', nombre: 'Uruguay', bandera: '🇺🇾' },
  { dial: '+595', nombre: 'Paraguay', bandera: '🇵🇾' },
  { dial: '+58', nombre: 'Venezuela', bandera: '🇻🇪' },
  { dial: '+55', nombre: 'Brasil', bandera: '🇧🇷' },
  { dial: '+52', nombre: 'México', bandera: '🇲🇽' },
  { dial: '+34', nombre: 'España', bandera: '🇪🇸' },
  { dial: '+1', nombre: 'Estados Unidos', bandera: '🇺🇸' },
]

export const PAIS_TELEFONO_DEFECTO = PAISES_TELEFONO[0]

/** Cuántos dígitos tiene un celular normal en cada país —para el
 *  chequeo rápido de "esto tiene pinta de número real" mientras se
 *  espera la verificación automática de WhatsApp Business (que
 *  requiere una cuenta de Meta verificada, así que por ahora esto es
 *  lo que hay). Chile es el único que se valida estricto porque es
 *  case el 90%+ de los clientes; el resto se deja como un largo
 *  razonable —exigir el formato exacto de trece países distintos
 *  generaría más rechazos falsos que errores reales pescados. */
const LARGO_EXACTO: Partial<Record<string, number>> = {
  '+56': 9, // 9 XXXX XXXX
}
const LARGO_MINIMO_GENERICO = 7
const LARGO_MAXIMO_GENERICO = 12

/** True cuando el número local tiene una cantidad de dígitos que tiene
 *  sentido para el país elegido. No confirma que el número exista de
 *  verdad ni que tenga WhatsApp —eso es lo que haría la verificación
 *  automática—, solo descarta los casos obvios (le faltan dígitos, se
 *  copió mal, quedó a medio escribir). */
export function telefonoLuceValido(dial: string, numero: string): boolean {
  const digitos = numero.trim().replace(/[^\d]/g, '')
  const exacto = LARGO_EXACTO[dial]
  if (exacto !== undefined) return digitos.length === exacto
  return digitos.length >= LARGO_MINIMO_GENERICO && digitos.length <= LARGO_MAXIMO_GENERICO
}

/** Separa un teléfono guardado ("+56912345678") en su prefijo de país
 *  y el número local, para precargar el selector al editar un cliente
 *  que ya tenía el dato. Si no matchea ningún prefijo conocido (o no
 *  hay número todavía), se asume Chile —el caso por lejos más común—
 *  con lo que haya escrito como número local. */
export function separarTelefono(valor?: string): { dial: string; numero: string } {
  const limpio = (valor ?? '').trim()
  if (!limpio) return { dial: PAIS_TELEFONO_DEFECTO.dial, numero: '' }
  if (!limpio.startsWith('+')) return { dial: PAIS_TELEFONO_DEFECTO.dial, numero: limpio }

  // Prefijos más largos primero: "+598" no debe leerse como "+59" de
  // nada (no existe) ni confundirse con uno más corto que calce antes.
  const ordenados = [...PAISES_TELEFONO].sort((a, b) => b.dial.length - a.dial.length)
  const match = ordenados.find((p) => limpio.startsWith(p.dial))
  if (!match) return { dial: PAIS_TELEFONO_DEFECTO.dial, numero: limpio.slice(1) }
  return { dial: match.dial, numero: limpio.slice(match.dial.length).trim() }
}

/** Arma el teléfono completo a partir del prefijo elegido y lo que se
 *  haya escrito, listo para guardar. Sin espacios en el número local
 *  para que quede igual al formato que ya usa el resto de la base
 *  ("+56985274239").
 *
 *  Ojo: si todavía no se ha escrito ningún dígito, de todas formas
 *  devuelve el prefijo solo (por ejemplo "+54") en vez de una cadena
 *  vacía. Si devolviera '', el próximo render volvería a leer "sin
 *  teléfono" y el selector de país se resetearía solo a Chile —típico
 *  cuando alguien elige el país primero y recién después escribe el
 *  número. */
export function unirTelefono(dial: string, numero: string): string {
  const local = numero.trim().replace(/[^\d]/g, '')
  return `${dial}${local}`
}
